(function(root, factory) {
  var api = factory(root);

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }

  if (root) {
    root.WallForgeDesignPersonalization = api;
  }

  if (api && typeof api.start === 'function') {
    if (root && root.document && typeof root.setTimeout === 'function' && typeof api.scan === 'function') {
      root.setTimeout(function() { api.scan(root.document); }, 0);
    }
    api.start();
  }
})(typeof window !== 'undefined' ? window : globalThis, function(root) {
  'use strict';

  var CONTRACT = 'wallforge.design-personalization.public@v1';
  var KEY_PATTERN = /^[a-z][a-z0-9_]{0,63}$/;
  var COLOR_PATTERN = /^#[0-9A-Fa-f]{6}$/;
  var RESERVED_KEYS = { __proto__: true, prototype: true, constructor: true };
  var PREVIEW_CLASSES = {
    classic_serif: 'wf-personalization-choice__preview--classic-serif',
    modern: 'wf-personalization-choice__preview--modern-sans',
    modern_sans: 'wf-personalization-choice__preview--modern-sans',
    script: 'wf-personalization-choice__preview--script'
  };
  var OPTION_SELECTION_LABELS = {
    classic_serif: 'Classic Serif',
    modern: 'Modern Sans',
    modern_sans: 'Modern Sans',
    script: 'Handwritten'
  };
  var MAX_FIELDS = 12;
  var MAX_OPTIONS = 32;
  var MAX_CART_BYTES = 255;
  var MAX_PUBLIC_SCHEMA_BYTES = 64 * 1024;
  var controllers = typeof WeakMap !== 'undefined' ? new WeakMap() : new Map();
  var instanceCounter = 0;
  var started = false;

  function isObject(value) {
    return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
  }

  function cleanString(value, maxLength) {
    if (typeof value !== 'string') return '';
    var normalized = value.trim();
    return typeof maxLength === 'number' ? normalized.slice(0, maxLength) : normalized;
  }

  function boundedString(value, maxLength) {
    if (typeof value !== 'string') return '';
    var normalized = value.trim();
    return normalized.length <= maxLength ? normalized : '';
  }

  function byteLength(value) {
    var text = String(value == null ? '' : value);
    if (typeof TextEncoder !== 'undefined') {
      return new TextEncoder().encode(text).length;
    }
    try {
      return unescape(encodeURIComponent(text)).length;
    } catch (_error) {
      return text.length;
    }
  }

  function graphemes(value) {
    var text = String(value == null ? '' : value);
    if (typeof Intl !== 'undefined' && typeof Intl.Segmenter === 'function') {
      var segmenter = new Intl.Segmenter(undefined, { granularity: 'grapheme' });
      return Array.from(segmenter.segment(text), function(entry) { return entry.segment; });
    }

    var clusters = [];
    Array.from(text).forEach(function(character) {
      var codePoint = character.codePointAt(0);
      var isCombiningMark = /[\u0300-\u036f\u1ab0-\u1aff\u1dc0-\u1dff\u20d0-\u20ff\ufe20-\ufe2f]/.test(character);
      var isVariationSelector = (codePoint >= 0xfe00 && codePoint <= 0xfe0f)
        || (codePoint >= 0xe0100 && codePoint <= 0xe01ef);
      var isEmojiModifier = codePoint >= 0x1f3fb && codePoint <= 0x1f3ff;
      var joinsPrevious = character === '\u200d'
        || isCombiningMark
        || isVariationSelector
        || isEmojiModifier
        || (clusters.length && clusters[clusters.length - 1].slice(-1) === '\u200d');

      if (joinsPrevious && clusters.length) {
        clusters[clusters.length - 1] += character;
      } else {
        clusters.push(character);
      }
    });
    return clusters;
  }

  function normalizeText(value) {
    var text = String(value == null ? '' : value).trim();
    return typeof text.normalize === 'function' ? text.normalize('NFC') : text;
  }

  function deepFreeze(value) {
    if (!value || typeof value !== 'object' || Object.isFrozen(value)) return value;
    Object.keys(value).forEach(function(key) { deepFreeze(value[key]); });
    return Object.freeze(value);
  }

  function safeIdToken(value) {
    return String(value == null ? '' : value).replace(/[^a-zA-Z0-9_-]/g, '-');
  }

  function createElement(tagName, className, textValue) {
    var element = root.document.createElement(tagName);
    if (className) element.className = className;
    if (textValue !== undefined && textValue !== null) element.textContent = String(textValue);
    return element;
  }

  function dispatch(uploaderRoot, eventName, detail) {
    if (!uploaderRoot || typeof root.CustomEvent !== 'function') return;
    uploaderRoot.dispatchEvent(new root.CustomEvent(eventName, {
      bubbles: true,
      detail: detail || {}
    }));
  }

  function parseOption(rawOption, fieldType) {
    if (!isObject(rawOption)) throw new Error('invalid_option');
    var idSource = typeof rawOption.id === 'string' ? rawOption.id.trim() : '';
    var labelSource = typeof rawOption.label === 'string' ? rawOption.label.trim() : '';
    if (idSource.length > 64 || labelSource.length > 80) throw new Error('invalid_option');
    var id = idSource;
    var label = labelSource;
    if (!KEY_PATTERN.test(id) || RESERVED_KEYS[id] || !label) throw new Error('invalid_option');

    var presentation = isObject(rawOption.presentation) ? rawOption.presentation : {};
    var option = { id: id, label: label, presentation: {} };

    if (fieldType === 'swatch') {
      var swatch = isObject(presentation.swatch) ? presentation.swatch : {};
      if (swatch.kind !== 'color' || !COLOR_PATTERN.test(String(swatch.value || ''))) {
        throw new Error('invalid_swatch');
      }
      option.presentation.swatch = { kind: 'color', value: String(swatch.value) };
    } else {
      var sampleSource = typeof presentation.sample === 'string' ? presentation.sample.trim() : 'Aa';
      var previewTokenSource = typeof presentation.previewToken === 'string' ? presentation.previewToken.trim() : '';
      if (sampleSource.length > 12 || previewTokenSource.length > 64) throw new Error('invalid_option_presentation');
      option.presentation.sample = sampleSource || 'Aa';
      option.presentation.previewToken = previewTokenSource;
      if (option.presentation.previewToken && !PREVIEW_CLASSES[option.presentation.previewToken]) {
        throw new Error('invalid_preview_token');
      }
    }

    return option;
  }

  function parseField(rawField, index) {
    if (!isObject(rawField)) throw new Error('invalid_field');
    var keySource = typeof rawField.key === 'string' ? rawField.key.trim() : '';
    var typeSource = typeof rawField.type === 'string' ? rawField.type.trim() : '';
    var labelSource = typeof rawField.label === 'string' ? rawField.label.trim() : '';
    if (keySource.length > 64 || typeSource.length > 32 || labelSource.length > 80) throw new Error('invalid_field');
    var key = keySource;
    var type = typeSource;
    var label = labelSource;
    if (!KEY_PATTERN.test(key) || RESERVED_KEYS[key] || !label) throw new Error('invalid_field');
    if (type !== 'short_text' && type !== 'swatch' && type !== 'choice') {
      throw new Error('unsupported_field');
    }

    var presentation = isObject(rawField.presentation) ? rawField.presentation : {};
    var cart = isObject(rawField.cart) ? rawField.cart : {};
    var cartLabelSource = typeof cart.label === 'string' && cart.label.trim() ? cart.label.trim() : label;
    if (!cartLabelSource || cartLabelSource.length > 80 || byteLength(cartLabelSource) > MAX_CART_BYTES) {
      throw new Error('invalid_cart_label');
    }
    var field = {
      key: key,
      type: type,
      label: label,
      required: rawField.required === true,
      presentation: {
        order: Number.isFinite(Number(presentation.order)) ? Number(presentation.order) : index,
        placeholder: cleanString(presentation.placeholder, 120),
        helpText: cleanString(presentation.helpText || rawField.helpText, 240),
        showCounter: presentation.showCounter === true,
        style: cleanString(presentation.style, 32)
      },
      cart: {
        label: cartLabelSource,
        visible: cart.visible !== false
      },
      declarationOrder: index
    };

    if (type === 'short_text') {
      var constraints = isObject(rawField.constraints) ? rawField.constraints : {};
      var maxGraphemes = Number(constraints.maxGraphemes || 64);
      var characterPolicy = cleanString(constraints.characterPolicy, 64);
      if (!Number.isInteger(maxGraphemes) || maxGraphemes < 1 || maxGraphemes > 64) {
        throw new Error('invalid_text_limit');
      }
      if (characterPolicy && characterPolicy !== 'person_name') throw new Error('unsupported_character_policy');
      field.constraints = {
        maxGraphemes: maxGraphemes,
        characterPolicy: characterPolicy,
        singleLine: constraints.singleLine !== false
      };
      if (rawField.defaultValue != null && typeof rawField.defaultValue !== 'string') throw new Error('invalid_default');
      field.defaultValue = rawField.defaultValue == null ? null : rawField.defaultValue;
    } else {
      if (!Array.isArray(rawField.options) || rawField.options.length < 1 || rawField.options.length > MAX_OPTIONS) {
        throw new Error('invalid_options');
      }
      var seenOptions = Object.create(null);
      field.options = rawField.options.map(function(option) {
        var parsed = parseOption(option, type);
        if (seenOptions[parsed.id]) throw new Error('duplicate_option');
        seenOptions[parsed.id] = true;
        return parsed;
      });
      var defaultOptionSource = typeof rawField.defaultOptionId === 'string' ? rawField.defaultOptionId.trim() : '';
      if (defaultOptionSource.length > 64) throw new Error('invalid_default');
      field.defaultOptionId = defaultOptionSource;
      if (field.defaultOptionId && !seenOptions[field.defaultOptionId]) throw new Error('invalid_default');
      if (field.required && !field.defaultOptionId) throw new Error('missing_default');
    }

    return field;
  }

  function parseSchema(rawSchema) {
    if (typeof rawSchema === 'string' && byteLength(rawSchema) > MAX_PUBLIC_SCHEMA_BYTES) {
      throw new Error('schema_too_large');
    }
    var source = typeof rawSchema === 'string' ? JSON.parse(rawSchema) : rawSchema;
    if (byteLength(typeof rawSchema === 'string' ? rawSchema : JSON.stringify(source || null)) > MAX_PUBLIC_SCHEMA_BYTES) {
      throw new Error('schema_too_large');
    }
    if (!isObject(source) || source.contract !== CONTRACT || source.enabled !== true) {
      throw new Error('unsupported_contract');
    }
    if (!Array.isArray(source.fields) || source.fields.length > MAX_FIELDS) {
      throw new Error('invalid_fields');
    }

    var schemaId = typeof source.schemaId === 'string' ? source.schemaId.trim() : '';
    var version = source.version;
    var hash = typeof source.hash === 'string' ? source.hash.trim() : '';
    if (!schemaId || schemaId.length > 80 || typeof version !== 'number' || !Number.isInteger(version) || version < 1 || !/^sha256:[a-f0-9]{64}$/.test(hash)) {
      throw new Error('invalid_identity');
    }

    var rawUi = isObject(source.ui) ? source.ui : {};
    var title = typeof rawUi.title === 'string' ? rawUi.title.trim() : '';
    var disclaimer = typeof rawUi.disclaimer === 'string' ? rawUi.disclaimer.trim() : '';
    if (title.length > 80 || disclaimer.length > 240) throw new Error('invalid_ui');

    var seenFields = Object.create(null);
    var seenCartLabels = Object.create(null);
    var reservedCartLabels = {
      '_custom design': true,
      '_print file': true,
      '_custom subject type': true
    };
    var fields = source.fields.map(function(rawField, index) {
      var field = parseField(rawField, index);
      if (seenFields[field.key]) throw new Error('duplicate_field');
      seenFields[field.key] = true;
      var normalizedCartLabel = field.cart.label.toLowerCase();
      if (reservedCartLabels[normalizedCartLabel] || normalizedCartLabel.indexOf('_wallforge_') === 0 || seenCartLabels[normalizedCartLabel]) {
        throw new Error('invalid_cart_label');
      }
      seenCartLabels[normalizedCartLabel] = true;
      return field;
    }).sort(function(left, right) {
      return (left.presentation.order - right.presentation.order) || (left.declarationOrder - right.declarationOrder);
    });

    return {
      contract: CONTRACT,
      enabled: true,
      schemaId: schemaId,
      version: version,
      hash: hash,
      ui: {
        title: title || 'Make it yours',
        disclaimer: disclaimer
      },
      fields: fields
    };
  }

  function createController(uploaderRoot, personalizationRoot, schema) {
    var instanceId = 'wf-personalization-' + (++instanceCounter) + '-' + safeIdToken(personalizationRoot.dataset.productId || 'product');
    var presentation = personalizationRoot.dataset.wfPersonalizationPresentation === 'commission-brief'
      ? 'commission-brief'
      : 'card';
    var isCommissionBrief = presentation === 'commission-brief';
    var controls = Object.create(null);
    var controlGroups = Object.create(null);
    var errors = Object.create(null);
    var selectedLabels = Object.create(null);
    var counters = Object.create(null);
    var listeners = [];
    var locked = false;
    var canonical = null;
    var artwork = null;
    var lastCartBinding = '';
    var lastCartJobId = '';
    var previousRevisionInCart = false;
    var acceptedRevisionCheckpoint = null;
    var destroyed = false;

    function addListener(node, name, handler) {
      node.addEventListener(name, handler);
      listeners.push({ node: node, name: name, handler: handler });
    }

    function fieldErrorId(field) {
      return instanceId + '-' + safeIdToken(field.key) + '-error';
    }

    function clearErrors() {
      Object.keys(errors).forEach(function(key) {
        errors[key].hidden = true;
        errors[key].textContent = '';
        if (controls[key]) controls[key].removeAttribute('aria-invalid');
        (controlGroups[key] || []).forEach(function(control) { control.removeAttribute('aria-invalid'); });
      });
      var summary = personalizationRoot.querySelector('[data-wf-personalization-error-summary]');
      if (summary) {
        summary.hidden = true;
        summary.textContent = '';
      }
    }

    function setFieldError(field, message) {
      if (!errors[field.key]) return;
      errors[field.key].textContent = message;
      errors[field.key].hidden = false;
      if (controls[field.key]) controls[field.key].setAttribute('aria-invalid', 'true');
      (controlGroups[field.key] || []).forEach(function(control) { control.setAttribute('aria-invalid', 'true'); });
    }

    function valueFor(field) {
      if (field.type === 'short_text') return controls[field.key].value;
      var checked = personalizationRoot.querySelector('input[name="' + instanceId + '-' + field.key + '"]:checked');
      return checked ? checked.value : '';
    }

    function updateFieldPresentation(field) {
      var value = valueFor(field);
      if (field.type === 'short_text' && counters[field.key]) {
        counters[field.key].textContent = graphemes(value).length + '/' + field.constraints.maxGraphemes;
      }
      if (field.type !== 'short_text' && selectedLabels[field.key]) {
        var selected = field.options.find(function(option) { return option.id === value; });
        var previewToken = selected && selected.presentation ? selected.presentation.previewToken : '';
        selectedLabels[field.key].textContent = selected
          ? (OPTION_SELECTION_LABELS[previewToken] || selected.label)
          : 'Choose one';
      }
    }

    function validateText(field, rawValue) {
      var value = normalizeText(rawValue);
      if (!value) {
        return field.required
          ? { valid: false, value: null, message: field.label + ' is required.' }
          : { valid: true, value: null };
      }
      if (field.constraints.singleLine && /[\r\n]/.test(value)) {
        return { valid: false, value: value, message: field.label + ' must be one line.' };
      }
      if (graphemes(value).length > field.constraints.maxGraphemes) {
        return { valid: false, value: value, message: field.label + ' is too long.' };
      }
      if (byteLength(value) > 256) {
        return { valid: false, value: value, message: field.label + ' is too long.' };
      }
      if (field.constraints.characterPolicy === 'person_name') {
        try {
          if (!/^[\p{L}\p{M}\p{N} .'\u2019\-]*$/u.test(value)) {
            return { valid: false, value: value, message: 'Use letters, numbers, spaces, apostrophes, periods, or hyphens.' };
          }
        } catch (_error) {
          if (/[^A-Za-z0-9 .'\u2019\-]/.test(value)) {
            return { valid: false, value: value, message: 'Use letters, numbers, spaces, apostrophes, periods, or hyphens.' };
          }
        }
      }
      return { valid: true, value: value };
    }

    function collect(reportValidity) {
      clearErrors();
      var values = Object.create(null);
      var display = [];
      var requestedCartKeys = [];
      var invalid = [];

      schema.fields.forEach(function(field) {
        var rawValue = valueFor(field);
        var result;
        if (field.type === 'short_text') {
          result = validateText(field, rawValue);
        } else {
          var option = field.options.find(function(candidate) { return candidate.id === rawValue; });
          result = option
            ? { valid: true, value: option.id, valueLabel: option.label }
            : { valid: !field.required, value: null, message: field.label + ' is required.' };
        }

        if (!result.valid) {
          invalid.push({ field: field, message: result.message || 'Check this value.' });
          return;
        }

        values[field.key] = result.value;
        if (result.value !== null) {
          display.push({
            key: field.key,
            label: field.label,
            valueLabel: result.valueLabel || result.value
          });
          if (field.cart.visible) requestedCartKeys.push(field.cart.label);
        }
      });

      if (reportValidity && invalid.length) {
        invalid.forEach(function(item) { setFieldError(item.field, item.message); });
        var summary = personalizationRoot.querySelector('[data-wf-personalization-error-summary]');
        if (summary) {
          summary.textContent = 'Please check the highlighted personalization options.';
          summary.hidden = false;
        }
        var firstControl = controls[invalid[0].field.key];
        if (firstControl && typeof firstControl.focus === 'function') firstControl.focus();
      }

      return {
        ok: invalid.length === 0,
        payload: invalid.length ? null : deepFreeze({
          schemaId: schema.schemaId,
          schemaVersion: schema.version,
          schemaHash: schema.hash,
          values: values
        }),
        displayCandidates: display,
        requestedCartPropertyKeys: requestedCartKeys,
        errors: invalid.map(function(item) { return { field: item.field.key, message: item.message }; })
      };
    }

    function renderSummary() {
      var targets = uploaderRoot.querySelectorAll('[data-step="success"], [data-step="accepted"]');
      targets.forEach(function(target) {
        var summaryHost = target.querySelector('[data-wf-personalization-summary-slot]') || target;
        var summary = target.querySelector('[data-wf-personalization-summary]');
        if (!canonical || !canonical.summary.length) {
          if (summary) summary.remove();
          return;
        }
        if (!summary) {
          summary = createElement('section', 'wf-personalization-summary');
          summary.setAttribute('data-wf-personalization-summary', '');
          summary.setAttribute('aria-label', 'Your personalization');
        }
        if (summary.parentNode !== summaryHost) summaryHost.appendChild(summary);
        summary.textContent = '';
        summary.appendChild(createElement('p', 'wf-personalization-summary__title', 'Your personalization'));
        var list = createElement('dl');
        canonical.summary.forEach(function(item) {
          list.appendChild(createElement('dt', '', item.label));
          list.appendChild(createElement('dd', '', item.valueLabel));
        });
        summary.appendChild(list);
        if (previousRevisionInCart) {
          var notice = createElement('p', 'wf-personalization-field__error', 'Your cart may still contain an earlier version. Remove it before purchasing this edited design.');
          notice.setAttribute('role', 'alert');
          notice.appendChild(root.document.createTextNode(' '));
          var cartLink = createElement('a', '', 'View cart');
          cartLink.href = '/cart';
          notice.appendChild(cartLink);
          summary.appendChild(notice);
        }
      });
    }

    function render() {
      personalizationRoot.textContent = '';
      personalizationRoot.classList.toggle('wf-personalization-host--commission-brief', isCommissionBrief);
      var container = personalizationRoot;

      if (isCommissionBrief) {
        personalizationRoot.setAttribute('role', 'group');
        personalizationRoot.setAttribute('aria-label', schema.ui.title);
      } else {
        personalizationRoot.removeAttribute('role');
        personalizationRoot.removeAttribute('aria-label');
        container = createElement('section', 'wf-personalization-card');
        container.setAttribute('aria-labelledby', instanceId + '-title');
        var title = createElement('h3', 'wf-personalization-card__title', schema.ui.title);
        title.id = instanceId + '-title';
        container.appendChild(title);
      }

      var errorSummary = createElement('p', 'wf-personalization-error-summary');
      errorSummary.setAttribute('data-wf-personalization-error-summary', '');
      errorSummary.setAttribute('role', 'status');
      errorSummary.setAttribute('aria-live', 'polite');
      errorSummary.hidden = true;
      container.appendChild(errorSummary);
      schema.fields.forEach(function(field) {
        var errorId = fieldErrorId(field);
        var error = createElement('p', 'wf-personalization-field__error');
        error.id = errorId;
        error.hidden = true;
        errors[field.key] = error;

        if (field.type === 'short_text') {
          var wrapper = createElement('div', 'wf-personalization-field');
          var heading = createElement('div', 'wf-personalization-field__heading');
          var label = createElement('label', 'wf-personalization-field__label', field.label);
          var inputId = instanceId + '-' + safeIdToken(field.key);
          label.setAttribute('for', inputId);
          heading.appendChild(label);
          if (field.presentation.showCounter) {
            var counter = createElement('span', 'wf-personalization-field__counter', '0/' + field.constraints.maxGraphemes);
            counter.id = inputId + '-counter';
            counters[field.key] = counter;
            heading.appendChild(counter);
          }
          wrapper.appendChild(heading);
          var input = createElement('input', 'wf-personalization-field__input');
          input.type = 'text';
          input.required = field.required;
          input.id = inputId;
          input.value = field.defaultValue || '';
          input.placeholder = field.presentation.placeholder || '';
          input.setAttribute('autocomplete', 'off');
          input.setAttribute('aria-describedby', [field.presentation.showCounter ? inputId + '-counter' : '', errorId].filter(Boolean).join(' '));
          controls[field.key] = input;
          wrapper.appendChild(input);
          if (field.presentation.helpText) wrapper.appendChild(createElement('p', 'wf-personalization-field__help', field.presentation.helpText));
          wrapper.appendChild(error);
          addListener(input, 'input', function() {
            updateFieldPresentation(field);
            clearErrors();
            dispatch(uploaderRoot, 'wallforge:personalization-change', {
              productId: personalizationRoot.dataset.productId || '',
              schemaId: schema.schemaId,
              valid: collect(false).ok
            });
          });
          container.appendChild(wrapper);
          updateFieldPresentation(field);
          return;
        }

        var fieldset = createElement('fieldset', 'wf-personalization-fieldset');
        fieldset.setAttribute('aria-required', field.required ? 'true' : 'false');
        var legend = createElement('legend');
        var fieldHeading = createElement('span', 'wf-personalization-field__heading');
        fieldHeading.appendChild(createElement('span', 'wf-personalization-field__label', field.label));
        var selectedLabel = createElement('span', 'wf-personalization-field__selection', 'Choose one');
        selectedLabel.setAttribute('aria-live', 'polite');
        selectedLabels[field.key] = selectedLabel;
        fieldHeading.appendChild(selectedLabel);
        legend.appendChild(fieldHeading);
        fieldset.appendChild(legend);
        var options = createElement('div', 'wf-personalization-options' + (field.type === 'choice' ? ' wf-personalization-options--cards' : ''));
        field.options.forEach(function(option) {
          var optionId = instanceId + '-' + safeIdToken(field.key) + '-' + safeIdToken(option.id);
          var choice = createElement('label', 'wf-personalization-choice wf-personalization-choice--' + (field.type === 'swatch' ? 'swatch' : 'card'));
          choice.setAttribute('for', optionId);
          choice.setAttribute('title', option.label);
          var radio = createElement('input', 'wf-personalization-native-choice');
          radio.type = 'radio';
          radio.required = field.required;
          radio.name = instanceId + '-' + field.key;
          radio.id = optionId;
          radio.value = option.id;
          radio.setAttribute('aria-describedby', errorId);
          radio.checked = Boolean(field.defaultOptionId && option.id === field.defaultOptionId);
          controls[field.key] = controls[field.key] || radio;
          controlGroups[field.key] = controlGroups[field.key] || [];
          controlGroups[field.key].push(radio);
          choice.appendChild(radio);
          if (field.type === 'swatch') {
            var swatch = createElement('span', 'wf-personalization-swatch');
            swatch.style.backgroundColor = option.presentation.swatch.value;
            swatch.setAttribute('aria-hidden', 'true');
            choice.appendChild(swatch);
            choice.appendChild(createElement('span', 'visually-hidden', option.label));
          } else {
            var preview = createElement('span', 'wf-personalization-choice__preview', option.presentation.sample);
            preview.setAttribute('aria-hidden', 'true');
            if (option.presentation.previewToken) preview.classList.add(PREVIEW_CLASSES[option.presentation.previewToken]);
            choice.appendChild(preview);
            choice.appendChild(createElement('span', 'wf-personalization-choice__label', option.label));
          }
          addListener(radio, 'change', function() {
            updateFieldPresentation(field);
            clearErrors();
            dispatch(uploaderRoot, 'wallforge:personalization-change', {
              productId: personalizationRoot.dataset.productId || '',
              schemaId: schema.schemaId,
              valid: collect(false).ok
            });
          });
          options.appendChild(choice);
        });
        fieldset.appendChild(options);
        if (field.presentation.helpText) fieldset.appendChild(createElement('p', 'wf-personalization-field__help', field.presentation.helpText));
        fieldset.appendChild(error);
        container.appendChild(fieldset);
        updateFieldPresentation(field);
      });

      if (!isCommissionBrief) {
        var resetButton = createElement('button', 'wf-personalization-reset', 'Reset personalization');
        resetButton.type = 'button';
        resetButton.setAttribute('data-wf-personalization-reset', '');
        addListener(resetButton, 'click', function() { reset(); });
        container.appendChild(resetButton);
        if (schema.ui.disclaimer) container.appendChild(createElement('p', 'wf-personalization-card__disclaimer', schema.ui.disclaimer));
        personalizationRoot.appendChild(container);
      }
      personalizationRoot.dataset.wfPersonalizationState = 'ready';
    }

    function snapshot(options) {
      return collect(Boolean(options && options.reportValidity));
    }

    function applyServerErrors(fieldErrors) {
      if (!Array.isArray(fieldErrors) || !fieldErrors.length) return false;
      clearErrors();
      var firstControl = null;
      var applied = 0;
      var messages = {
        REQUIRED: function(field) { return field.label + ' is required.'; },
        TOO_LONG: function(field) { return field.label + ' is too long.'; },
        INVALID_OPTION: function(field) { return 'Choose an available ' + field.label.toLowerCase() + ' option.'; },
        UNSUPPORTED_CHARACTERS: function() { return 'Use only the supported characters for this field.'; },
        UNKNOWN_FIELD: function(field) { return 'Check ' + field.label.toLowerCase() + '.'; },
        INVALID_VALUE: function(field) { return 'Check ' + field.label.toLowerCase() + '.'; }
      };

      fieldErrors.slice(0, MAX_FIELDS).forEach(function(item) {
        if (!isObject(item) || typeof item.path !== 'string' || typeof item.code !== 'string') return;
        var match = item.path.match(/^personalization\.values\.([a-z][a-z0-9_]{0,63})$/);
        if (!match || !messages[item.code]) return;
        var field = schema.fields.find(function(candidate) { return candidate.key === match[1]; });
        if (!field) return;
        setFieldError(field, messages[item.code](field));
        if (!firstControl) firstControl = controls[field.key];
        applied += 1;
      });

      if (!applied) return false;
      var summary = personalizationRoot.querySelector('[data-wf-personalization-error-summary]');
      if (summary) {
        summary.textContent = 'Please check the highlighted personalization options.';
        summary.hidden = false;
      }
      if (firstControl && typeof firstControl.focus === 'function') firstControl.focus();
      return true;
    }

    function reconcile(serverPersonalization) {
      if (!isObject(serverPersonalization)) return false;
      if (serverPersonalization.schemaId !== schema.schemaId || serverPersonalization.schemaVersion !== schema.version) return false;
      if (serverPersonalization.schemaHash !== schema.hash) return false;
      if (!/^hmac-sha256:[a-f0-9]{64}$/.test(String(serverPersonalization.personalizationHash || ''))) return false;
      if (!Array.isArray(serverPersonalization.summary) || !isObject(serverPersonalization.cartProperties)) return false;
      if (serverPersonalization.summary.length > MAX_FIELDS || Object.keys(serverPersonalization.cartProperties).length > MAX_FIELDS) return false;

      var summary = [];
      var seenSummaryKeys = Object.create(null);
      for (var summaryIndex = 0; summaryIndex < serverPersonalization.summary.length; summaryIndex += 1) {
        var item = serverPersonalization.summary[summaryIndex];
        if (!isObject(item)) return false;
        var label = boundedString(item.label || item.cartLabel, 80);
        var valueLabel = typeof item.valueLabel === 'string' ? item.valueLabel : '';
        var summaryKey = boundedString(item.key, 64);
        if (!KEY_PATTERN.test(summaryKey) || !label || !valueLabel || byteLength(valueLabel) > MAX_CART_BYTES) return false;
        if (seenSummaryKeys[summaryKey] || !schema.fields.some(function(field) { return field.key === summaryKey; })) return false;
        seenSummaryKeys[summaryKey] = true;
        summary.push({ key: summaryKey, label: label, valueLabel: valueLabel });
      }

      var cartProperties = Object.create(null);
      var cartKeys = Object.keys(serverPersonalization.cartProperties);
      for (var cartIndex = 0; cartIndex < cartKeys.length; cartIndex += 1) {
        var key = cartKeys[cartIndex];
        var value = serverPersonalization.cartProperties[key];
        if (!key || key.charAt(0) === '_' || typeof value !== 'string') return false;
        if (byteLength(key) > MAX_CART_BYTES || byteLength(value) > MAX_CART_BYTES) return false;
        cartProperties[key] = value;
      }

      canonical = {
        personalizationHash: String(serverPersonalization.personalizationHash),
        summary: summary,
        cartProperties: cartProperties
      };
      renderSummary();
      return true;
    }

    function setArtwork(serverArtwork, jobId, serverPersonalization) {
      if (!isObject(serverArtwork)) return false;
      if (arguments.length >= 3) {
        if (!isObject(serverPersonalization)) return false;
        if (serverPersonalization.schemaId !== schema.schemaId) return false;
        if (serverPersonalization.schemaVersion !== schema.version) return false;
        if (!canonical || String(serverPersonalization.personalizationHash || '') !== canonical.personalizationHash) return false;
      }
      var revisionId = boundedString(serverArtwork.revisionId, 96);
      var binding = boundedString(serverArtwork.binding, 240);
      var normalizedJobId = boundedString(jobId, 128);
      if (!revisionId || !normalizedJobId || !/^wfab1_[A-Za-z0-9_-]{16,220}$/.test(binding)) return false;
      if (lastCartJobId === normalizedJobId && lastCartBinding && lastCartBinding !== binding) {
        previousRevisionInCart = true;
      }
      artwork = { revisionId: revisionId, binding: binding, jobId: normalizedJobId };
      renderSummary();
      return true;
    }

    function getCartState(jobId) {
      var normalizedJobId = boundedString(jobId, 128);
      if (acceptedRevisionCheckpoint && canonical !== acceptedRevisionCheckpoint.canonical) return null;
      if (!canonical || !canonical.personalizationHash || !artwork || !artwork.binding || !normalizedJobId) return null;
      if (artwork.jobId !== normalizedJobId || previousRevisionInCart) return null;
      var properties = Object.create(null);
      Object.keys(canonical.cartProperties).forEach(function(key) { properties[key] = canonical.cartProperties[key]; });
      properties._wallforge_job_id = normalizedJobId;
      properties._wallforge_artwork_binding = artwork.binding;
      properties._wallforge_personalization_version = String(schema.version);
      properties._wallforge_personalization_hash = canonical.personalizationHash;
      var propertyKeys = Object.keys(properties);
      for (var propertyIndex = 0; propertyIndex < propertyKeys.length; propertyIndex += 1) {
        var propertyKey = propertyKeys[propertyIndex];
        if (byteLength(propertyKey) > MAX_CART_BYTES || byteLength(properties[propertyKey]) > MAX_CART_BYTES) return null;
      }
      return {
        properties: properties,
        trackingProperties: {
          _wallforge_job_id: normalizedJobId,
          _wallforge_artwork_binding: artwork.binding,
          _wallforge_personalization_version: String(schema.version),
          _wallforge_personalization_hash: canonical.personalizationHash
        },
        revisionId: artwork.revisionId,
        binding: artwork.binding
      };
    }

    function markCartAdded(jobId) {
      var normalizedJobId = boundedString(jobId, 128);
      if (artwork && artwork.binding && artwork.jobId === normalizedJobId) {
        lastCartBinding = artwork.binding;
        lastCartJobId = normalizedJobId;
      }
    }

    async function canAddCurrentRevision(jobId) {
      var normalizedJobId = boundedString(jobId, 128);
      if (!previousRevisionInCart) return Boolean(getCartState(normalizedJobId));
      if (!artwork || artwork.jobId !== normalizedJobId || typeof root.fetch !== 'function') return false;

      try {
        var response = await root.fetch('/cart.js', { cache: 'no-store', credentials: 'same-origin' });
        if (!response.ok) return false;
        var cart = await response.json();
        var items = Array.isArray(cart && cart.items) ? cart.items : [];
        var hasPreviousRevision = items.some(function(item) {
          var properties = isObject(item && item.properties) ? item.properties : {};
          return String(properties._wallforge_job_id || '') === normalizedJobId
            && String(properties._wallforge_artwork_binding || '') !== artwork.binding;
        });
        previousRevisionInCart = hasPreviousRevision;
        renderSummary();
        return !hasPreviousRevision && Boolean(getCartState(normalizedJobId));
      } catch (_error) {
        return false;
      }
    }

    function lock() {
      locked = true;
      personalizationRoot.classList.add('is-locked');
      personalizationRoot.querySelectorAll('input, button').forEach(function(control) { control.disabled = true; });
    }

    function unlock(options) {
      locked = false;
      personalizationRoot.classList.remove('is-locked');
      personalizationRoot.querySelectorAll('input, button').forEach(function(control) { control.disabled = false; });
      if (!options || options.preserveValues !== true) reset();
    }

    function beginDraft(options) {
      if (!acceptedRevisionCheckpoint && canonical && artwork) {
        acceptedRevisionCheckpoint = {
          canonical: canonical,
          artwork: artwork,
          previousRevisionInCart: previousRevisionInCart
        };
      }
      if (!options || options.unlock !== false) {
        unlock({ preserveValues: true });
      }
      return Boolean(acceptedRevisionCheckpoint);
    }

    function restoreAcceptedRevision(options) {
      if (!acceptedRevisionCheckpoint) return false;
      canonical = acceptedRevisionCheckpoint.canonical;
      artwork = acceptedRevisionCheckpoint.artwork;
      previousRevisionInCart = acceptedRevisionCheckpoint.previousRevisionInCart;
      acceptedRevisionCheckpoint = null;
      renderSummary();
      if (options && options.unlock === true) {
        unlock({ preserveValues: true });
      } else {
        lock();
      }
      return true;
    }

    function commitDraft() {
      acceptedRevisionCheckpoint = null;
      renderSummary();
    }

    function clearAttempt(options) {
      canonical = null;
      artwork = null;
      previousRevisionInCart = false;
      acceptedRevisionCheckpoint = null;
      renderSummary();
      unlock({ preserveValues: !options || options.preserveValues !== false });
    }

    function reset() {
      schema.fields.forEach(function(field) {
        if (field.type === 'short_text') {
          controls[field.key].value = field.defaultValue || '';
        } else {
          personalizationRoot.querySelectorAll('input[name="' + instanceId + '-' + field.key + '"]').forEach(function(radio) {
            radio.checked = Boolean(field.defaultOptionId && radio.value === field.defaultOptionId);
          });
        }
        updateFieldPresentation(field);
      });
      clearErrors();
      dispatch(uploaderRoot, 'wallforge:personalization-change', {
        productId: personalizationRoot.dataset.productId || '',
        schemaId: schema.schemaId,
        valid: collect(false).ok
      });
    }

    function destroy() {
      listeners.forEach(function(listener) { listener.node.removeEventListener(listener.name, listener.handler); });
      listeners = [];
      destroyed = true;
      controllers.delete(uploaderRoot);
    }

    render();

    var controller = {
      isReady: function() { return !destroyed; },
      isLocked: function() { return locked; },
      snapshot: snapshot,
      applyServerErrors: applyServerErrors,
      reconcile: reconcile,
      setArtwork: setArtwork,
      getCartState: getCartState,
      markCartAdded: markCartAdded,
      canAddCurrentRevision: canAddCurrentRevision,
      hasPreviousRevisionInCart: function() { return previousRevisionInCart; },
      lock: lock,
      unlock: unlock,
      beginDraft: beginDraft,
      restoreAcceptedRevision: restoreAcceptedRevision,
      commitDraft: commitDraft,
      clearAttempt: clearAttempt,
      reset: reset,
      destroy: destroy,
      schema: schema
    };

    return controller;
  }

  function findPersonalizationRoot(uploaderRoot) {
    return uploaderRoot && uploaderRoot.querySelector
      ? uploaderRoot.querySelector('[data-wf-personalization-root]')
      : null;
  }

  function failClosed(uploaderRoot, personalizationRoot) {
    personalizationRoot.dataset.wfPersonalizationState = 'error';
    var message = personalizationRoot.querySelector('[data-wf-personalization-boot-message]');
    if (!message) {
      message = createElement('p', 'wf-personalization-boot wf-personalization-boot--error');
      message.setAttribute('data-wf-personalization-boot-message', '');
      personalizationRoot.appendChild(message);
    }
    message.textContent = 'Personalization is temporarily unavailable. Please try again later.';
    message.setAttribute('role', 'alert');
    dispatch(uploaderRoot, 'wallforge:personalization-unavailable', {
      productId: personalizationRoot.dataset.productId || ''
    });
  }

  function mount(uploaderRoot) {
    if (!uploaderRoot || !uploaderRoot.querySelector) return null;
    if (controllers.has(uploaderRoot)) return controllers.get(uploaderRoot);
    var personalizationRoot = findPersonalizationRoot(uploaderRoot);
    if (!personalizationRoot) return null;
    if (personalizationRoot.dataset.wfPersonalizationUnavailable === 'true') {
      failClosed(uploaderRoot, personalizationRoot);
      return null;
    }
    var schemaElement = personalizationRoot.querySelector('[data-wf-personalization-schema]');
    if (!schemaElement) {
      failClosed(uploaderRoot, personalizationRoot);
      return null;
    }
    try {
      var schema = parseSchema(schemaElement.textContent || '');
      var controller = createController(uploaderRoot, personalizationRoot, schema);
      controllers.set(uploaderRoot, controller);
      dispatch(uploaderRoot, 'wallforge:personalization-ready', {
        productId: personalizationRoot.dataset.productId || '',
        schemaId: schema.schemaId,
        valid: controller.snapshot({ reportValidity: false }).ok
      });
      return controller;
    } catch (_error) {
      failClosed(uploaderRoot, personalizationRoot);
      return null;
    }
  }

  function get(uploaderRoot) {
    return controllers.get(uploaderRoot) || null;
  }

  function scan(node) {
    if (!root || !root.document) return;
    var scope = node && node.querySelectorAll ? node : root.document;
    if (scope.matches && (scope.matches('.custom-image-uploader') || scope.matches('.wf-multi-uploader'))) mount(scope);
    scope.querySelectorAll('.custom-image-uploader, .wf-multi-uploader').forEach(function(uploaderRoot) { mount(uploaderRoot); });
  }

  function start() {
    if (started || !root || !root.document) return;
    started = true;
    if (root.document.readyState === 'loading') {
      root.document.addEventListener('DOMContentLoaded', function() { scan(root.document); }, { once: true });
    } else {
      scan(root.document);
    }
    root.document.addEventListener('shopify:section:load', function(event) { scan(event.target); });
    root.document.addEventListener('shopify:section:unload', function(event) {
      if (!event.target || !event.target.querySelectorAll) return;
      event.target.querySelectorAll('.custom-image-uploader, .wf-multi-uploader').forEach(function(uploaderRoot) {
        var controller = get(uploaderRoot);
        if (controller) controller.destroy();
      });
    });
    if (typeof root.MutationObserver === 'function') {
      new root.MutationObserver(function(records) {
        records.forEach(function(record) {
          record.addedNodes.forEach(function(node) {
            if (node && node.nodeType === 1) scan(node);
          });
        });
      }).observe(root.document.documentElement, { childList: true, subtree: true });
    }
  }

  return {
    CONTRACT: CONTRACT,
    parseSchema: parseSchema,
    graphemes: graphemes,
    start: start,
    mount: mount,
    get: get,
    scan: scan
  };
});
